import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  role: 'cleaner' | 'supervisor' | 'admin';
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  role: 'cleaner' | 'supervisor' | 'admin' | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  hardResetAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function normalizeRole(value: unknown): UserProfile['role'] {
  return value === 'admin' || value === 'supervisor' || value === 'cleaner'
    ? value
    : 'cleaner';
}

function buildFallbackProfile(user: User | null): UserProfile | null {
  if (!user) {
    return null;
  }

  return {
    id: user.id,
    email: user.email ?? '',
    full_name: (user.user_metadata?.full_name as string | undefined) ?? null,
    role: normalizeRole(user.user_metadata?.role),
  };
}

function clearSupabaseAuthStorage(): void {
  const clearFromStorage = (storage: Storage | null) => {
    if (!storage) {
      return;
    }

    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (!key) {
        continue;
      }

      const shouldRemove =
        key === 'supabase.auth.token'
        || /^sb-[\w-]+-auth-token$/i.test(key)
        || key.startsWith('supabase.auth.');

      if (shouldRemove) {
        storage.removeItem(key);
      }
    }
  };

  try {
    clearFromStorage(window.localStorage);
  } catch {
    // no-op
  }

  try {
    clearFromStorage(window.sessionStorage);
  } catch {
    // no-op
  }
}

async function loadProfileFromOpsApi(session: Session | null): Promise<UserProfile | null> {
  const user = session?.user ?? null;
  if (!user || !session?.access_token) {
    return null;
  }

  try {
    const response = await fetch('/api/ops/auth/me', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    const payload = await response.json() as {
      role?: unknown;
      profile?: { full_name?: unknown } | null;
    };

    return {
      id: user.id,
      email: user.email ?? '',
      full_name: typeof payload.profile?.full_name === 'string' ? payload.profile.full_name : null,
      role: normalizeRole(payload.role),
    };
  } catch {
    return null;
  }
}

async function loadProfile(session: Session | null): Promise<UserProfile | null> {
  const user = session?.user ?? null;
  if (!user) {
    return null;
  }

  const fallback = buildFallbackProfile(user);
  const serverProfile = await loadProfileFromOpsApi(session);
  if (serverProfile) {
    return {
      ...serverProfile,
      full_name: serverProfile.full_name ?? fallback?.full_name ?? null,
    };
  }

  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, full_name, role')
      .eq('id', user.id)
      .maybeSingle();

    if (error || !data) {
      return fallback;
    }

    return {
      id: data.id,
      email: user.email ?? '',
      full_name: data.full_name ?? fallback?.full_name ?? null,
      role: normalizeRole(data.role),
    };
  } catch {
    return fallback;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const failOpenTimer = window.setTimeout(() => {
      if (cancelled) {
        return;
      }

      setIsLoading(false);
    }, 4000);

    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        if (cancelled) {
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        setProfile(await loadProfile(session ?? null));
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setSession(null);
        setUser(null);
        setProfile(null);
      })
      .finally(() => {
        if (!cancelled) {
          window.clearTimeout(failOpenTimer);
          setIsLoading(false);
        }
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      try {
        if (cancelled) {
          return;
        }

        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        setProfile(await loadProfile(nextSession ?? null));
      } catch {
        if (cancelled) {
          return;
        }

        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        setProfile(buildFallbackProfile(nextSession?.user ?? null));
      } finally {
        if (!cancelled) {
          window.clearTimeout(failOpenTimer);
          setIsLoading(false);
        }
      }
    });

    return () => {
      cancelled = true;
      window.clearTimeout(failOpenTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch {
      // continue with local cleanup
    }

    clearSupabaseAuthStorage();
    setSession(null);
    setUser(null);
    setProfile(null);
  };

  const hardResetAuth = async () => {
    await signOut();
    window.location.hash = '/dashboard';
    window.location.reload();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        role: profile?.role ?? null,
        isLoading,
        signIn,
        signOut,
        hardResetAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
